import React from "react";
import { get } from "lodash";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { stateRequirementsTable } from "statics/stateTaxRequirements";
import type { RootState } from "types/state";

import FederalTax from "components/FederalTax";
import type { FederalTaxSelection } from "./distributionTypes";

interface OwnProps {
  // react-step-wizard injects goToNamedStep at runtime (the workflows omit it);
  // optional in the contract, non-null-asserted at call sites (house pattern).
  goToNamedStep?: (step: string) => void;
  onNext?: (...args: unknown[]) => void;
  userState?: string;
  federalTaxSelection: FederalTaxSelection;
  onFederalTaxSelection: (selection: FederalTaxSelection) => void;
  isRothDistribution?: boolean;
  // react-step-wizard injects stepName at runtime; the workflows declare it
  // on the JSX element.
  stepName?: string;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const { id, profile: userInfo } = state.user;

  return {
    id,
    userInfo,
    ownProps,
  };
};

const mapDispatchToProps = {
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  federalTaxSelection: FederalTaxSelection;
}

export class DistributionFederalTax extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      federalTaxSelection: {
        federalTaxWithholdingPercentage: "",
        percentage: "",
      },
    };
  }

  // FederalTax types this callback as `(...args: unknown[]) => void`; narrow the
  // first arg to the FederalTaxSelection shape the component actually emits.
  handleFederalTaxWithholdingPercentage = (...args: unknown[]) => {
    const { federalTaxWithholdingPercentage, percentage } =
      args[0] as FederalTaxSelection;
    this.props.onFederalTaxSelection({
      federalTaxWithholdingPercentage,
      percentage,
    });
  };

  handleCancel = () => {
    if (this.props.isRothDistribution) {
      this.props.goToNamedStep!("distributionType");
    } else {
      this.props.goToNamedStep!("distributionReason");
    }
  };

  goToNextStep = () => {
    const { userState } = this.props;
    const stateData = get(stateRequirementsTable, userState as string);

    const isMandatoryOptOut =
      stateData &&
      stateData.option === "Mandatory Opt Out" &&
      stateData.canOptOut;

    if (isMandatoryOptOut) {
      this.props.goToNamedStep!("distributionSummary");
    } else {
      this.props.goToNamedStep!("distributionStateTax");
    }
  };

  render() {
    const { federalTaxSelection } = this.props;
    const { federalTaxWithholdingPercentage, percentage } = federalTaxSelection;

    const isButtonDisabled = !federalTaxWithholdingPercentage || !percentage;

    return (
      <FederalTax
        isRothDistribution={this.props.isRothDistribution}
        federalTaxSelection={this.state.federalTaxSelection}
        federalTaxWithholdingPercentage={
          this.handleFederalTaxWithholdingPercentage
        }
        onCancel={this.handleCancel}
        onNext={this.goToNextStep}
        disabled={isButtonDisabled}
      />
    );
  }
}

export default connector(withApollo<Props>(DistributionFederalTax));
